// src/components/Sidebar.tsx - Enhanced light mode separation

import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Calendar, 
  BarChart3, 
  FileText, 
  StickyNote, 
  TrendingUp, 
  Sparkles, 
  Settings, 
  LogOut,
  Share2,
  Sun,
  Moon,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useTheme } from '../../../context/ThemeContext';
import { useLayout } from '../../../context/LayoutContext';

const Sidebar: React.FC = () => {
  const { logout, hasSubscription, isTrial, user } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const { sidebarCollapsed, toggleSidebar, zoomLevel, setZoomLevel } = useLayout();
  const location = useLocation();
  
  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Zoom levels - more granular
  const zoomLevels = [70, 80, 90, 100, 110, 120, 130, 140, 150];
  const currentZoomIndex = zoomLevels.indexOf(zoomLevel);

  const handleZoomIn = () => {
    if (currentZoomIndex < zoomLevels.length - 1) {
      setZoomLevel(zoomLevels[currentZoomIndex + 1]);
    }
  };

  const handleZoomOut = () => {
    if (currentZoomIndex > 0) {
      setZoomLevel(zoomLevels[currentZoomIndex - 1]);
    }
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  const handleLogout = async () => {
    await logout();
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Calculate trial days (same logic for both versions)
  const trialDaysLeft = (() => {
    if (hasSubscription) return 0;
    
    try {
      const trialEnd = localStorage.getItem('trialEndDate');
      if (!trialEnd) return 0;
      
      const endDate = new Date(trialEnd);
      const now = new Date();
      
      if (isNaN(endDate.getTime()) || isNaN(now.getTime())) {
        return 0;
      }
      
      const diffTime = endDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return Math.max(0, diffDays);
    } catch (error) {
      return 0;
    }
  })();

  // FIXED: Check for both subscription AND trial access
  const hasFullAccess = hasSubscription || isTrial;

  // SIDEBAR CONTENT with dynamic width and text visibility
  const SidebarContent = () => (
    <div className={`h-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 border-r border-gray-200 dark:border-gray-700 md:rounded-r-3xl shadow-[10px_0_50px_-5px_rgba(0,0,0,0.25)] flex flex-col relative transition-all duration-300`}>
      {/* Glass effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-white/5 dark:from-gray-700/30 dark:to-gray-700/5 backdrop-blur-sm md:rounded-r-3xl pointer-events-none"></div>
      <div className="absolute inset-0 bg-gradient-to-tr from-primary-500/10 to-transparent md:rounded-r-3xl pointer-events-none"></div>
      
      {/* COLLAPSE BUTTON - Desktop only */}
      <button
        onClick={toggleSidebar}
        className="hidden md:flex absolute -right-3 top-8 w-6 h-6 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full items-center justify-center shadow-md hover:shadow-lg transition-all duration-200 z-20"
        aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {sidebarCollapsed ? (
          <ChevronRight size={14} className="text-gray-700 dark:text-gray-300" />
        ) : (
          <ChevronLeft size={14} className="text-gray-700 dark:text-gray-300" />
        )}
      </button>

      {/* SCROLLABLE CONTENT */}
      <div className="flex-1 overflow-y-auto p-6 relative z-10">
        {/* LOGO/TITLE */}
        <div className="mb-8 mt-12 md:mt-0">
          {!sidebarCollapsed ? (
            <div className="text-left">
              <h1 className="text-2xl font-display font-bold gradient-text leading-tight">
                <span className="block">Simplified</span>
                <span className="block">Trading</span>
                <span className="block">Journal</span>
              </h1>
            </div>
          ) : (
            <div className="text-center">
              <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center mx-auto">
                <TrendingUp size={20} className="text-white" />
              </div>
            </div>
          )}
        </div>
        <hr className="my-4 border-t-2 border-gray-300 dark:border-gray-700" />

        {/* TRIAL BANNER - only show if not subscribed and not collapsed */}
        {!hasSubscription && trialDaysLeft >= 0 && !sidebarCollapsed && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-6">
            <h3 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">Free Trial</h3>
            <p className="text-sm text-amber-700 dark:text-amber-300">
              {trialDaysLeft > 0 ? `${trialDaysLeft} days left` : 'Trial expired'}
            </p>
            <Link 
              to="/pricing" 
              className="mt-3 block w-full text-center bg-amber-600 hover:bg-amber-700 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors"
            >
              Upgrade Now
            </Link>
          </div>
        )}

        {/* NAVIGATION with conditional text */}
        <nav className="space-y-2">
          <Link
            to="/app/calendar"
            className={`flex items-center ${sidebarCollapsed ? 'justify-center' : ''} py-3 px-4 rounded-xl transition-colors ${
              isActive('/app/calendar')
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'text-gray-700 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-700/50'
            }`}
            title={sidebarCollapsed ? "Calendar" : undefined}
          >
            <Calendar className={sidebarCollapsed ? '' : 'mr-3'} size={20} />
            {!sidebarCollapsed && 'Calendar'}
          </Link>

          {/* FIXED: Show all features for BOTH subscription AND trial users */}
          {hasFullAccess && (
            <>
              <Link
                to="/app/tradingmodels"
                className={`flex items-center ${sidebarCollapsed ? 'justify-center' : ''} py-3 px-4 rounded-xl transition-colors ${
                  isActive('/app/tradingmodels')
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-700/50'
                }`}
                title={sidebarCollapsed ? "Trading Models" : undefined}
              >
                <TrendingUp className={sidebarCollapsed ? '' : 'mr-3'} size={20} />
                {!sidebarCollapsed && 'Trading Models'}
              </Link>

              <Link
                to="/app/tradelog"
                className={`flex items-center ${sidebarCollapsed ? 'justify-center' : ''} py-3 px-4 rounded-xl transition-colors ${
                  isActive('/app/tradelog')
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-700/50'
                }`}
                title={sidebarCollapsed ? "Trade Log" : undefined}
              >
                <FileText className={sidebarCollapsed ? '' : 'mr-3'} size={20} />
                {!sidebarCollapsed && 'Trade Log'}
              </Link>

              <Link
                to="/app/notes"
                className={`flex items-center ${sidebarCollapsed ? 'justify-center' : ''} py-3 px-4 rounded-xl transition-colors ${
                  isActive('/app/notes')
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-700/50'
                }`}
                title={sidebarCollapsed ? "Notes" : undefined}
              >
                <StickyNote className={sidebarCollapsed ? '' : 'mr-3'} size={20} />
                {!sidebarCollapsed && 'Notes'}
              </Link>

              <Link
                to="/app/dashboard"
                className={`flex items-center ${sidebarCollapsed ? 'justify-center' : ''} py-3 px-4 rounded-xl transition-colors ${
                  isActive('/app/dashboard')
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-700/50'
                }`}
                title={sidebarCollapsed ? "Dashboard" : undefined}
              >
                <BarChart3 className={sidebarCollapsed ? '' : 'mr-3'} size={20} />
                {!sidebarCollapsed && 'Dashboard'}
              </Link>

              <Link
                to="/app/ai-analysis"
                className={`flex items-center ${sidebarCollapsed ? 'justify-center' : ''} py-3 px-4 rounded-xl transition-colors ${
                  isActive('/app/ai-analysis')
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-white/70 dark:hover:bg-gray-700/50'
                }`}
                title={sidebarCollapsed ? "AI Trading Coach" : undefined}
              >
                <Sparkles className={sidebarCollapsed ? '' : 'mr-3'} size={20} />
                {!sidebarCollapsed && 'AI Trading Coach'}
              </Link>
            </>
          )}
        </nav>
      </div>

      {/* BOTTOM SECTION - Enhanced visual separation in light mode */}
      <div className="p-6 border-t-2 border-gray-300 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30 relative z-10">
        {/* THEME TOGGLE */}
        <button
          onClick={toggleTheme}
          className={`flex items-center ${sidebarCollapsed ? 'justify-center' : ''} w-full py-3 px-4 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-white/70 dark:hover:bg-gray-700/50 transition-colors mb-2`}
          title={sidebarCollapsed ? (isDarkMode ? 'Light Mode' : 'Dark Mode') : undefined}
        >
          {isDarkMode ? <Sun className={sidebarCollapsed ? '' : 'mr-3'} size={20} /> : <Moon className={sidebarCollapsed ? '' : 'mr-3'} size={20} />}
          {!sidebarCollapsed && (isDarkMode ? 'Light Mode' : 'Dark Mode')}
        </button>

        {/* ZOOM CONTROLS - Enhanced for better light mode visibility */}
        {!sidebarCollapsed ? (
          <div className="mb-2 py-3 px-4 rounded-xl bg-white/50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-600">
            <div className="flex items-center gap-3">
              <ZoomIn size={16} className="text-gray-700 dark:text-gray-300" />
              <select
                value={zoomLevel}
                onChange={(e) => setZoomLevel(parseInt(e.target.value))}
                className="flex-1 bg-transparent border-0 text-sm font-medium text-gray-700 dark:text-gray-300 focus:outline-none cursor-pointer"
              >
                {zoomLevels.map(level => (
                  <option key={level} value={level} className="bg-white dark:bg-gray-800">
                    {level}%
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <div className="mb-2 py-2 text-center">
            <div className="text-xs text-gray-600 dark:text-gray-400">{zoomLevel}%</div>
          </div>
        )}
        
        {!sidebarCollapsed && (
          <Link
            to="/app/team"
            className="flex items-center py-3 px-4 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-white/70 dark:hover:bg-gray-700/50 transition-colors mb-2"
          >
            <Share2 className="mr-3" size={20} />
            Share it with friends and earn 40%
          </Link>
        )}
        
        <Link
          to="/app/settings"
          className={`flex items-center ${sidebarCollapsed ? 'justify-center' : ''} py-3 px-4 rounded-xl transition-colors mb-2 ${
            isActive('/app/settings')
              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
              : 'text-gray-700 dark:text-gray-300 hover:bg-white/70 dark:hover:bg-gray-700/50'
          }`}
          title={sidebarCollapsed ? "Settings" : undefined}
        >
          <Settings className={sidebarCollapsed ? '' : 'mr-3'} size={20} />
          {!sidebarCollapsed && 'Settings'}
        </Link>
        
        <button
          onClick={handleLogout}
          className={`flex items-center ${sidebarCollapsed ? 'justify-center' : ''} w-full py-3 px-4 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-white/70 dark:hover:bg-gray-700/50 transition-colors`}
          title={sidebarCollapsed ? "Sign Out" : undefined}
        >
          <LogOut className={sidebarCollapsed ? '' : 'mr-3'} size={20} />
          {!sidebarCollapsed && 'Sign Out'}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* MOBILE BURGER BUTTON */}
      <button
        onClick={toggleMobileMenu}
        className="fixed top-4 left-4 z-[1001] md:hidden bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-3 rounded-xl shadow-lg border border-gray-200 dark:border-gray-600"
        aria-label="Toggle menu"
      >
        {isMobileMenuOpen ? (
          <X size={24} className="text-gray-700 dark:text-gray-300" />
        ) : (
          <Menu size={24} className="text-gray-700 dark:text-gray-300" />
        )}
      </button>

      {/* MOBILE BACKDROP */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-[999] md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* SIDEBAR - DYNAMIC WIDTH */}
      <div 
        ref={sidebarRef}
        className={`
          fixed top-0 left-0 h-full z-[1000] transition-all duration-300 ease-in-out
          ${sidebarCollapsed ? 'w-16' : 'w-64'}
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
      >
        <SidebarContent />
      </div>
    </>
  );
};

export { Sidebar };